"use client"

import { useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button, type ButtonProps } from "@/components/ui/button"
import { useUndoQualityChangeMutation } from "@/hooks/mutations/conversation-quality"
import { refusalNote } from "@/lib/conversation-quality/change-display"
import type { SuggestionKind } from "@/lib/conversation-quality/suggestions/schema"

/** Undo of one change, behind its confirm, which stays open until the undo settles. */
export function UndoChangeButton({
  suggestionId,
  kind,
  label,
  size,
  onUndone,
}: {
  suggestionId: string
  kind: SuggestionKind
  label: string
  size?: ButtonProps["size"]
  onUndone?: () => void | Promise<void>
}) {
  const [confirming, setConfirming] = useState(false)
  const undo = useUndoQualityChangeMutation({
    onUndone: async () => {
      await onUndone?.()
      setConfirming(false)
    },
  })

  return (
    <>
      <Button type="button" variant="outline" size={size} onClick={() => setConfirming(true)}>
        <span>{label}</span>
      </Button>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Undo this change?</AlertDialogTitle>
            <AlertDialogDescription>{refusalNote(kind, "undo")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undo.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={undo.isPending}
              onClick={(event) => {
                // Radix closes the dialog on this click; it stays open until the undo settles.
                event.preventDefault()
                undo.mutateAsync({ input: { suggestionId } }).catch(() => {})
              }}
            >
              Undo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
